import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { withVariants } from 'inside-out/managers/componentManager';
import { idToLink } from 'inside-out/managers/navManager';
import { Cards } from 'inside-out/components/shared/cards';
import { Carousel } from 'inside-out/components/shared/carousel';
import { CarouselCard } from 'inside-out/components/shared/cards/carouselCard';
import { ColouredCard } from 'inside-out/components/shared/cards/colouredCard';
import { ELearningCard } from 'inside-out/components/shared/cards/eLearningCard';
import { NewsCard } from 'inside-out/components/shared/cards/newsCard';
import { ArrowLink } from 'inside-out/components/shared/arrowLink';
import { GetInvolved } from 'inside-out/components/shared/getInvolved';
import MyselfBG from 'inside-out/images/register/myself-bg.svg';
import SomeoneIKnowBG from 'inside-out/images/register/someone-i-know-bg.svg';
import HealthProfessionalBG from 'inside-out/images/register/health-professional-bg.svg';
import ResearcherBG from 'inside-out/images/register/researcher-bg.svg';

import {
	blue,
	grey,
	purple,
	aqua,
	tabletLandscapeMaxWidthBreakpoint,
	tabletPortraitMaxWidthBreakpoint,
	mobileMaxWidthBreakpoint,
} from 'inside-out/global-styles';

const ColouredCardVariant = withVariants(ColouredCard, {
	[`max-width: ${tabletLandscapeMaxWidthBreakpoint}px`]: 'compressed',
	[`max-width: ${tabletPortraitMaxWidthBreakpoint}px`]: 'compressedOneColumn',
	[`max-width: ${mobileMaxWidthBreakpoint}px`]: 'minimized',
});
import HeroMobile from 'inside-out/images/home/hero-mobile.png';
import HeroAnimationH264 from 'inside-out/images/home/hero-h264.mp4';
import HeroAnimationVP8 from 'inside-out/images/home/hero-vp8.webm';

import {
	heroStyle,
	heroBackgroundStyle,
	heroTitleStyle,
	heroBodyStyle,
	heroBodyCTAStyle,
	cardsStyle,
	whoAreWeSectionStyle,
	whoAreWeInnerStyle,
	areYouAtRiskSectionStyle,
	atRiskLeftStyle,
	atRiskRightStyle,
	resourceSectionStyle,
	resourceSectionInnerStyle,
	updatesSectionStyle,
	updatesInnerWrapperStyle,
	updatesSectionInnerStyle,
	titleStyle,
	bodyTextStyle,
	innerWrapperStyle,
	ourPartnersSectionStyle,
	ourPartnersLogosWrapperStyle,
} from 'inside-out/page-styles';

const ResourcePropType = PropTypes.shape({
	title: PropTypes.string,
	type: PropTypes.string,
	date: PropTypes.string,
	fields: PropTypes.shape({
		thumbnail_image: PropTypes.shape({
			childImageSharp: PropTypes.shape({
				resize: PropTypes.shape({
					src: PropTypes.string.isRequired,
				}).isRequired,
			}).isRequired,
		}).isRequired,
	}),
	attachments: PropTypes.arrayOf(
		PropTypes.shape({
			vimeo_url: PropTypes.string,
		})
	),
}).isRequired;

const UpdatesPropType = PropTypes.shape({
	id: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	date: PropTypes.string,
	start_time: PropTypes.string,
	end_time: PropTypes.string,
	extract: PropTypes.string.isRequired,
	fields: PropTypes.shape({
		thumbnail_image: PropTypes.shape({
			childImageSharp: PropTypes.shape({
				resize: PropTypes.shape({
					src: PropTypes.string.isRequired,
				}).isRequired,
			}).isRequired,
		}).isRequired,
	}),
});

export default class HomePage extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							heading: PropTypes.string.isRequired,
							heading_image: PropTypes.string.isRequired,
							body: PropTypes.string.isRequired,
							call_to_action: PropTypes.string.isRequired,
							updates: PropTypes.arrayOf(
								PropTypes.shape({
									news: UpdatesPropType,
									event: UpdatesPropType,
								})
							),
							content_heading_2: PropTypes.string.isRequired,
							content_block_2: PropTypes.string.isRequired,
							content_link_text_2: PropTypes.string.isRequired,
							content_link_url_2: PropTypes.string.isRequired,
							assessment_heading: PropTypes.string.isRequired,
							assessment_body: PropTypes.string.isRequired,
							button_text: PropTypes.string.isRequired,
							button_url: PropTypes.string.isRequired,
							partners_heading: PropTypes.string.isRequired,
							partner_logos: PropTypes.arrayOf(
								PropTypes.shape({
									logo: PropTypes.string.isRequired,
									alt_text: PropTypes.string,
									link_url: PropTypes.string,
								}).isRequired
							),
							resource_1: ResourcePropType,
							resource_2: ResourcePropType,
							resource_3: ResourcePropType,
						}),
					})
				),
			}),
		}),
	};

	render() {
		const pageSettings = this.props.data.page.edges[0].node;
		const resources = [pageSettings.resource_1, pageSettings.resource_2, pageSettings.resource_3];
		const updates = pageSettings.updates
			.map(u => u.news || u.event || u.blog || u.podcast || u.resource || u.research_trial || u.employment || u.research_position)
			.filter(u=>u != null);

		return (
			<React.Fragment>
				<Helmet title="Home" />
				<section className={heroStyle}>
					<div className={heroBackgroundStyle(HeroMobile)}>
						<video autoPlay="autoplay" loop="loop" muted="muted">
							<source src={HeroAnimationH264} type="video/mp4" codecs="avc1.42E01E"/>
							<source src={HeroAnimationVP8} type="video/webm" codecs="vp8"/>
						</video>
					</div>
					<div className={innerWrapperStyle}>
						<h1 className={heroTitleStyle}>{pageSettings.heading}</h1>
						<p className={heroBodyStyle}>{pageSettings.body}</p>
					</div>
				</section>
				<section className={cardsStyle}>
					<div className={innerWrapperStyle}>
						<p className={heroBodyCTAStyle}>{pageSettings.call_to_action}</p>
						<Cards
							columns={4}
							scrollable={false}
							columnsAt={{
								[`max-width: ${tabletPortraitMaxWidthBreakpoint}`]: 1,
							}}
						>
							<ColouredCardVariant
								id={0}
								to="/for-myself"
								background={`#F5A81E url(${MyselfBG}) no-repeat bottom right`}
								minHeight={210}
								title="I am here for myself"
								arrow={true}
							/>
							<ColouredCardVariant
								id={1}
								to="/carer"
								background={`#F26C22 url(${SomeoneIKnowBG}) no-repeat bottom right`}
								minHeight={210}
								title="Someone I know may need help"
								arrow={true}
							/>
							<ColouredCardVariant
								id={2}
								to="/health-professionals"
								background={`${blue} url(${HealthProfessionalBG}) no-repeat bottom right`}
								minHeight={210}
								title="I am a health professional"
								arrow={true}
							/>
							<ColouredCardVariant
								id={3}
								to="/research"
								background={`${purple} url(${ResearcherBG}) no-repeat bottom right`}
								minHeight={210}
								title="I am a researcher"
								arrow={true}
							/>
						</Cards>
					</div>
				</section>

				{updates.length > 0 && (
					<section className={updatesSectionStyle}>
						<div className={updatesInnerWrapperStyle}>
							<div className={updatesSectionInnerStyle}>
								<header>
									<h1 className={titleStyle}>Latest Updates</h1>
								</header>
								<Carousel>
									{updates.map((node, index) => {
										return (
											<CarouselCard
												key={index}
												to={idToLink(node.id)}
												title={node.title}
												body={node.extract || node.project_extract}
												date={node.date || node.start_time || node.recruitment_open_date}
												endDate={node.end_time || node.recruitment_close_date}
												background={
													node.fields && node.fields.thumbnail_image.childImageSharp.resize.src
												}
											/>
										);
									})}
								</Carousel>
							</div>
						</div>
					</section>
				)}

				<section className={whoAreWeSectionStyle}>
					<div className={innerWrapperStyle}>
						<div className={whoAreWeInnerStyle}>
							<h1 className={titleStyle}>{pageSettings.content_heading_2}</h1>
							<div className={bodyTextStyle}>
								<div dangerouslySetInnerHTML={{ __html: pageSettings.content_block_2 }} />
								<ArrowLink to={pageSettings.content_link_url_2}>
									{pageSettings.content_link_text_2}
								</ArrowLink>
							</div>
						</div>
					</div>
				</section>

				<section className={areYouAtRiskSectionStyle}>
					<div className={innerWrapperStyle}>
						<div className={atRiskLeftStyle}>
							<h1 className={titleStyle}>{pageSettings.assessment_heading}</h1>
							<div className={bodyTextStyle}>
								<div dangerouslySetInnerHTML={{ __html: pageSettings.assessment_body }} />
							</div>
						</div>
						<div className={atRiskRightStyle}>
							<ColouredCard
								id={1}
								to={pageSettings.button_url}
								background={aqua}
								minHeight={164}
								title={pageSettings.button_text}
								arrow={true}
							/>
						</div>
					</div>
				</section>

				<section className={resourceSectionStyle}>
					<div className={innerWrapperStyle}>
						<div className={resourceSectionInnerStyle}>
							<header>
								<h1 className={titleStyle}>Our Resources</h1>
								<ArrowLink to="/resource-library">View All Resources</ArrowLink>
							</header>
							<Cards columns={3}>
								{resources.map((node, index) => (
									<ELearningCard
										key={index}
										to={idToLink(node.id)}
										mediaImage={
											node.fields && node.fields.thumbnail_image.childImageSharp.resize.src
										}
										hasVideo={!!node.attachments.find(a => a.vimeo_url)}
										title={node.title}
										body={node.extract}
										linkText={node.tile_button_text || 'Watch the Demo'}
									/>
								))}
							</Cards>
						</div>
					</div>
				</section>

				<section className={ourPartnersSectionStyle}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={titleStyle}>{pageSettings.partners_heading}</h1>
						</header>
						<div className={ourPartnersLogosWrapperStyle}>
							{pageSettings.partner_logos.map((logo, index) => (
								<a key={index} href={logo.link_url} target="blank" rel="noopener noreferrer">
									<img src={logo.logo} alt={logo.alt_text} />
								</a>
							))}
						</div>
					</div>
				</section>

				<GetInvolved />
			</React.Fragment>
		);
	}
}

export const query = graphql`
	query HomePageQuery {
		page: allContentJson(filter: { id: { regex: "/content\\/home-page\\.json/" } }) {
			edges {
				node {
					heading
					heading_image
					body
					call_to_action
					updates {
						news {
							...newsItem
						}
						event {
							...event
						}
						blog {
							...blogItem
						}
						podcast {
							...podcastItem
						}
						resource {
							...resourceItem
						}
						research_trial {
							...researchTrialItem
						}
						employment {
							...employmentPositionItem
						}
						research_position {
							...researchPositionItem
						}
					}
					content_heading_2
					content_block_2
					content_link_text_2
					content_link_url_2
					assessment_heading
					assessment_body
					button_text
					button_url
					partners_heading
					partner_logos {
						logo
						alt_text
						link_url
					}
					resource_1 {
						...resource
					}
					resource_2 {
						...resource
					}
					resource_3 {
						...resource
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/pages/index.js